import { createFileRoute } from "@tanstack/react-router";
import Faqs from "@/pages/Faqs";
import { buildBreadcrumb, ldJson } from "@/lib/site-schema";

const faqCategories = [
    {
      category: "SEO Local",
      icon: "📍",
      questions: [
        {
          question: "¿Qué es el SEO Local y por qué lo necesito?",
          answer: "El SEO Local es la optimización para aparecer en búsquedas geográficas como 'restaurante cerca de mí' o 'abogado en Madrid'. Es esencial para negocios físicos ya que el 46% de búsquedas en Google son locales y el 76% de quienes buscan algo cercano visitan el negocio en 24 horas."
        },
        {
          question: "¿Cuánto tiempo tarda en dar resultados el SEO Local?",
          answer: "Los primeros resultados se ven en 4-8 semanas: mejora en el ranking de Google Maps y más visibilidad. Los resultados significativos (top 3 en búsquedas locales) se logran en 3-6 meses. El SEO Local es más rápido que el SEO tradicional porque la competencia es menor."
        },
        {
          question: "¿Qué incluye la optimización de Google Business Profile?",
          answer: "Incluimos: configuración completa del perfil, optimización de categorías y atributos, gestión de fotos profesionales, respuesta a reseñas, publicación de posts semanales, análisis de insights, y seguimiento del ranking en el pack local de Google."
        },
        {
          question: "¿Funciona el SEO Local para múltiples ubicaciones?",
          answer: "Sí, tenemos experiencia gestionando negocios con múltiples ubicaciones. Creamos y optimizamos perfiles independientes para cada sede, con estrategias específicas por zona y seguimiento individualizado de resultados."
        }
      ]
    },
    {
      category: "SEO Web",
      icon: "🌐",
      questions: [
        {
          question: "¿Qué diferencia hay entre SEO Local y SEO Web?",
          answer: "El SEO Local se enfoca en búsquedas geográficas y Google Maps. El SEO Web trabaja el posicionamiento orgánico nacional para keywords competitivas, construcción de autoridad del dominio, y visibilidad en todo el territorio. Son complementarios y recomendamos ambos."
        },
        {
          question: "¿Cuánto cuesta el SEO Web con IA?",
          answer: "Cada proyecto es único y la inversión depende de la competencia de tu sector, tus objetivos y el alcance del trabajo. Tras el diagnóstico de tu empresa, te presentamos una propuesta personalizada con inversión clara y resultados esperados. Incluye análisis inicial, optimización técnica, contenido y link building."
        },
        {
          question: "¿Qué garantías ofrecen en SEO Web?",
          answer: "Garantizamos algo más valioso que una posición concreta: el trabajo profesional completo necesario para competir por el top de tu sector. Esto incluye metodología probada, transparencia total con informes mensuales, equipo senior dedicado y mejoras medibles en tráfico orgánico. Nadie serio puede garantizar una posición exacta en Google — quien lo prometa, te miente o no entiende cómo funciona el algoritmo."
        },
        {
          question: "¿El SEO Web funciona para todos los sectores?",
          answer: "Sí, trabajamos con empresas de todos los sectores: salud, legal, ecommerce, servicios profesionales, tecnología, turismo, educación, etc. Nuestra IA se adapta a las particularidades de cada industria y sus patrones de búsqueda específicos."
        }
      ]
    },
    {
      category: "SEO para E-commerce",
      icon: "🛍️",
      questions: [
        {
          question: "¿Cómo mejora la IA el SEO de mi tienda online?",
          answer: "La IA analiza millones de datos para: optimizar automáticamente títulos y descripciones de productos, mejorar la arquitectura de categorías, identificar keywords long-tail rentables, detectar problemas técnicos, y ajustar estrategias según el comportamiento de usuarios."
        },
        {
          question: "¿Funciona para tiendas con miles de productos?",
          answer: "Sí, la IA es especialmente potente para catálogos grandes. Puede optimizar masivamente sin perder calidad, prioriza productos por rentabilidad potencial, y automatiza tareas que serían imposibles manualmente. Ideal para tiendas con 500+ productos."
        },
        {
          question: "¿Cuánto puede aumentar mis ventas el SEO para ecommerce?",
          answer: "Nuestros clientes ven incrementos promedio del 150-300% en tráfico orgánico en 6-12 meses, lo que se traduce en 40-120% más ventas. El ROI suele ser de 3-8€ por cada euro invertido en SEO una vez consolidada la estrategia."
        },
        {
          question: "¿Es compatible con Shopify, WooCommerce y otras plataformas?",
          answer: "Sí, trabajamos con todas las plataformas principales: Shopify, WooCommerce, PrestaShop, Magento, BigCommerce, etc. Nuestra metodología se adapta a las particularidades técnicas de cada CMS para maximizar resultados."
        }
      ]
    },
    {
      category: "Link Building",
      icon: "🔗",
      questions: [
        {
          question: "¿Qué es el Link Building y por qué es importante?",
          answer: "Link Building es conseguir enlaces de calidad desde otras webs hacia la tuya. Es uno de los 3 factores principales del algoritmo de Google. Los enlaces actúan como 'votos de confianza' que aumentan la autoridad de tu dominio y mejoran el posicionamiento."
        },
        {
          question: "¿Qué hace diferente al Link Building con IA?",
          answer: "Nuestra IA identifica automáticamente las mejores oportunidades de enlaces, analiza la calidad y relevancia de dominios, predice el impacto de cada enlace, y optimiza la estrategia en tiempo real. Es 10x más eficiente que el Link Building manual."
        },
        {
          question: "¿Cuántos enlaces necesito para ver resultados?",
          answer: "La calidad importa más que la cantidad. Generalmente, 5-10 enlaces de alta calidad/mes son suficientes para proyectos nuevos. Para sectores muy competitivos pueden necesitarse 15-30/mes. Nos enfocamos en enlaces que realmente muevan la aguja."
        },
        {
          question: "¿Los enlaces son permanentes?",
          answer: "Sí, trabajamos solo con enlaces permanentes, no temporales. Además, hacemos seguimiento mensual para asegurar que los enlaces se mantienen activos. Si alguno cae (raro), lo reemplazamos sin coste adicional."
        }
      ]
    },
    {
      category: "Publicidad SEM",
      icon: "📢",
      questions: [
        {
          question: "¿Cuánto hay que invertir en Google Ads?",
          answer: "Depende del coste por clic y la competencia de tu sector: las campañas necesitan volumen suficiente para que Google reúna datos y pueda optimizar. La inversión publicitaria se paga aparte, desde tu propia cuenta, y nuestros honorarios de gestión se concretan en la propuesta. En el diagnóstico estimamos qué volumen tiene sentido para tu caso."
        },
        {
          question: "¿Cuánto tiempo hasta ver resultados con SEM?",
          answer: "Los primeros clics y visitas se ven desde el día 1. Las conversiones iniciales en 1-2 semanas. La optimización completa de campañas toma 30-60 días, cuando la IA ha recopilado suficientes datos para maximizar el ROI."
        },
        {
          question: "¿Qué diferencia hay entre SEO y SEM?",
          answer: "El SEM es publicidad de pago en Google: resultados inmediatos pero requiere inversión continua. El SEO es posicionamiento orgánico: tarda más pero es sostenible a largo plazo. Son complementarios: el SEM genera ventas ya, el SEO construye activos duraderos."
        },
        {
          question: "¿La IA gestiona mis campañas completamente?",
          answer: "Sí, nuestra IA gestiona automáticamente: ajuste de pujas, optimización de anuncios, segmentación de audiencias, detección de keywords negativas, y redistribución de presupuesto. Tú recibes informes y nosotros supervisamos estratégicamente la IA."
        }
      ]
    },
    {
      category: "Desarrollo Web",
      icon: "💻",
      questions: [
        {
          question: "¿Por qué React es mejor para SEO que otros frameworks?",
          answer: "React con SSR (Server-Side Rendering) ofrece velocidad de carga excepcional, arquitectura SEO-friendly, fácil implementación de structured data, y compatibilidad perfecta con todas las herramientas de SEO. Es el framework más usado por empresas top por una razón."
        },
        {
          question: "¿Cuánto tiempo tarda en desarrollarse una web en React?",
          answer: "Una web corporativa simple: 2-4 semanas. Una web compleja con funcionalidades personalizadas: 6-12 semanas. Un ecommerce en Shopify: 3-8 semanas según catálogo. Incluye diseño, desarrollo, contenidos, testing y optimización SEO."
        },
        {
          question: "¿Incluye diseño responsive y optimización móvil?",
          answer: "Sí, todas nuestras webs son mobile-first: diseñadas primero para móvil y adaptadas a escritorio. El 60% del tráfico web es móvil, así que la experiencia móvil es prioritaria, no una adaptación posterior."
        },
        {
          question: "¿Puedo editar yo mismo el contenido después?",
          answer: "Sí, incluimos un CMS (sistema de gestión de contenidos) fácil de usar o integración con tu CMS preferido. Te damos formación para que puedas actualizar textos, imágenes y productos sin conocimientos técnicos."
        }
      ]
    },
    {
      category: "General",
      icon: "❓",
      questions: [
        {
          question: "¿Qué hace única a vuestra agencia SEO?",
          answer: "Somos la única agencia premium en España que combina SEO tradicional + IA avanzada + exclusividad blindada por contrato. Nuestra tecnología analiza 100x más datos que análisis manuales, identificamos oportunidades que otros no ven, y reservamos la exclusividad por sector y zona para un único cliente por sector y zona."
        },
        {
          question: "¿Cuál es el compromiso mínimo?",
          answer: "El compromiso mínimo es de 3 meses en SEO local y de 6 meses en GEO. Los mínimos de los demás servicios se indican en su propuesta. Estos compromisos no garantizan resultados en ese plazo; la continuidad posterior es opcional y a convenir. Definimos indicadores de seguimiento desde el primer mes."
        },
        {
          question: "¿Ofrecen informes y métricas transparentes?",
          answer: "Sí, recibes informes mensuales detallados con: rankings de keywords, tráfico orgánico, conversiones, enlaces conseguidos, trabajos realizados, y próximos pasos. Además, acceso 24/7 a un dashboard en tiempo real con todas las métricas."
        },
        {
          question: "¿Trabajan con empresas de mi sector?",
          answer: "Nuestra política de zonas exclusivas significa que solo trabajamos con un cliente por sector y zona geográfica. Si tu zona está disponible, podemos ayudarte. Si está ocupada, te lo diremos con transparencia. Consulta disponibilidad."
        },
        {
          question: "¿Por qué no garantizáis una posición concreta en Google?",
          answer: "Porque el algoritmo de Google no le pertenece a ninguna agencia. Cualquiera que prometa el 'puesto #1' está vendiéndote humo —y de hecho Google penaliza ese tipo de publicidad engañosa—. Lo que sí garantizamos —y es mucho más valioso— es ejecutar el trabajo profesional completo necesario para competir por las primeras posiciones de tu sector: equipo senior, IA propietaria, contenido, autoridad y optimización técnica."
        },
        {
          question: "¿Cuánto cuesta trabajar con SEOEVOLUTION?",
          answer: "Cada empresa necesita una estrategia propia. Diseñamos proyectos de SEO, visibilidad en Google Maps y posicionamiento en buscadores con IA según tu mercado, competencia y objetivos. No publicamos tarifas ni mínimos de inversión: alcance e inversión se concretan tras el diagnóstico. Si nuestro enfoque no encaja con tu situación, te lo decimos con transparencia."
        }
      ]
    }
  ];
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqCategories.flatMap((category) =>
    category.questions.map((q) => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer,
      },
    }))
  ),
};

const breadcrumbSchema = buildBreadcrumb([{ name: "Preguntas frecuentes", path: "/faqs" }]);

export const Route = createFileRoute("/faqs")({
  head: () => ({
    meta: [
      { title: "FAQs SEO | SEOEVOLUTION - Respuestas de Expertos" },
      {
        name: "description",
        content:
          "Resuelve tus dudas sobre SEO Local, Web, E-commerce, Link Building y SEM. Respuestas claras de expertos en marketing digital.",
      },
      {
        name: "keywords",
        content:
          "preguntas frecuentes SEO, FAQ SEO, dudas marketing digital, consultas SEO, ayuda SEO, preguntas Google Ads, FAQ marketing online",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://agenciaseoevolution.com/faqs" },
      { property: "og:title", content: "Preguntas Frecuentes sobre SEO y Marketing Digital | SEOEVOLUTION" },
      {
        property: "og:description",
        content:
          "Resuelve todas tus dudas sobre SEO Local, SEO Web, E-commerce, Link Building, SEM y desarrollo web con IA.",
      },
      { name: "twitter:title", content: "Preguntas Frecuentes sobre SEO y Marketing Digital | SEOEVOLUTION" },
      {
        name: "twitter:description",
        content:
          "Resuelve todas tus dudas sobre SEO Local, SEO Web, E-commerce, Link Building, SEM y desarrollo web con IA.",
      },
    ],
    links: [{ rel: "canonical", href: "https://agenciaseoevolution.com/faqs" }],
    scripts: [ldJson(breadcrumbSchema), { type: "application/ld+json", children: JSON.stringify(faqSchema) }],
  }),
  component: Faqs,
});
